'use client';

import React, { useState } from 'react';
import { Upload, Library, X, Image, Film, Music, FileText, Loader2 } from 'lucide-react';
import { MediaUploader } from './MediaUploader';
import type { MediaAsset, UploadFn, PollAssetFn } from './media-types';

type AcceptType = 'image' | 'audio' | 'video' | 'pdf' | 'all';

const TYPE_ICON: Record<string, React.ElementType> = {
  IMAGE: Image,
  AUDIO: Music,
  VIDEO: Film,
  DOCUMENT: FileText,
};

export interface MediaFieldProps {
  accept?: AcceptType;
  currentAsset?: MediaAsset | null;
  onSelect: (asset: MediaAsset) => void;
  onRemove?: () => void;
  label?: string;
  hint?: string;
  uploadFile: UploadFn;
  pollAsset?: PollAssetFn;
  renderLibraryPicker?: (args: { onSelect: (asset: MediaAsset) => void; onClose: () => void; }) => React.ReactNode;
}

export function MediaField({
  accept = 'all',
  currentAsset,
  onSelect,
  onRemove,
  label,
  hint,
  uploadFile,
  pollAsset,
  renderLibraryPicker,
}: MediaFieldProps) {
  const [mode, setMode] = useState<'upload' | 'library'>('upload');
  const [pickerOpen, setPickerOpen] = useState(false);

  const TypeIcon = currentAsset ? (TYPE_ICON[currentAsset.type] ?? Image) : null;

  return (
    <div className="space-y-2">
      {label && <p className="text-sm font-medium text-slate-700">{label}</p>}

      {/* Current asset preview */}
      {currentAsset && (
        <div className="flex items-center gap-3 p-2.5 border border-slate-200 rounded-lg bg-slate-50">
          {currentAsset.type === 'IMAGE' && currentAsset.url ? (
            <img
              src={currentAsset.url}
              alt=""
              className="h-10 w-10 object-cover rounded border bg-white shrink-0"
            />
          ) : (
            <div className="h-10 w-10 rounded border bg-white flex items-center justify-center shrink-0 text-slate-400">
              {TypeIcon && <TypeIcon size={16} />}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-700 truncate font-medium">
              {currentAsset.name || currentAsset.mimeType || currentAsset.type}
            </p>
            {currentAsset.status === 'PROCESSING' && (
              <p className="text-xs text-amber-600 flex items-center gap-1 mt-0.5">
                <Loader2 size={10} className="animate-spin" /> Sedang diproses...
              </p>
            )}
          </div>
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="p-1 text-slate-400 hover:text-red-500 transition-colors shrink-0"
              title="Hapus"
            >
              <X size={14} />
            </button>
          )}
        </div>
      )}

      {/* Mode toggle */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        <button
          type="button"
          onClick={() => { setMode('upload'); setPickerOpen(false); }}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
            mode === 'upload'
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Upload size={12} />
          Upload Baru
        </button>
        <button
          type="button"
          onClick={() => setMode('library')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
            mode === 'library'
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Library size={12} />
          Pilih dari Library
        </button>
      </div>

      {/* Upload mode */}
      {mode === 'upload' && (
        <MediaUploader
          accept={accept}
          hint={hint}
          onUploaded={onSelect}
          uploadFile={uploadFile}
          pollAsset={pollAsset}
        />
      )}

      {/* Library mode */}
      {mode === 'library' && (
        <>
          {renderLibraryPicker ? (
            <button type="button" onClick={() => setPickerOpen(true)}
              className="w-full border-2 border-dashed border-slate-300 hover:border-teal-400 rounded-xl p-6 text-center transition-colors group">
              <Library className="w-7 h-7 mx-auto mb-2 text-slate-400 group-hover:text-teal-500" />
              <p className="text-sm font-medium text-slate-600 group-hover:text-teal-700">Buka Media Library</p>
              <p className="text-xs text-slate-400 mt-1">Pilih media yang sudah diupload sebelumnya</p>
            </button>
          ) : null}
          {pickerOpen && renderLibraryPicker?.({
            onSelect: (asset) => { onSelect(asset); setPickerOpen(false); },
            onClose: () => setPickerOpen(false),
          })}
        </>
      )}
    </div>
  );
}
